import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import "./Result.css";

// Helper function to map card name to image path
const getCardImage = (cardName) => {
  if (!cardName) return null;
  // Map card name to card code format (e.g., "A" -> "A", "10" -> "10")
  const cardCode = cardName === "A" || cardName === "1" ? "A" : 
                   cardName === "J" ? "J" : 
                   cardName === "Q" ? "Q" : 
                   cardName === "K" ? "K" : 
                   cardName;
  // Use cards folder with .png extension
  return `/assets/img/cards/${cardCode}.png`;
};

// Helper function to extract card value from card code (e.g., "AHH" -> "A", "9CC" -> "9")
const extractCardValue = (cardCode) => {
  if (!cardCode) return null;
  const value = cardCode.replace(/(DD|SS|CC|HH)$/, '').trim();
  return value || null;
};

// Helper function to get card image from card code
const getCardImageFromCode = (cardCode) => {
  if (!cardCode) return null;
  // Use the full card code if available (e.g., "3HH.png"), otherwise extract value
  if (cardCode.match(/(DD|SS|CC|HH)$/)) {
    return `/assets/img/cards/${cardCode}.png`;
  }
  const value = extractCardValue(cardCode);
  if (!value) return null;
  return getCardImage(value);
};

// Helper function to map win value to result letter
const getWinLetter = (win) => {
  const winMap = {
    "0": "T", // Tie -> T
    "1": "L", // Low Card -> L
    "2": "H"  // High Card -> H
  };
  return winMap[win] || win;
};

// Helper function to parse rdesc for Lucky7: "High Card#Odd#Black#9#8 9 10"
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};

  const parts = rdesc.split('#');
  return {
    winner: parts[0] || "",
    oddEven: parts[1] || "",
    color: parts[2] || "",
    card: parts[3] || "",
    line: parts[4] || "",
  };
};

const ResultLucky7 = ({
  data = [],
  gameData = {},
  gameType = 'lucky7',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(gameType);
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            setLastResults(response.data.data.res);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            setLastResults(response.data.res);
          }
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [gameType]);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);

    const mid = resultItem?.mid;

    if (mid) {
      try {
        const response = await getDetailResults(gameType, mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  }, [gameType]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal || !detailResult) return null;

    const cardImage = getCardImageFromCode(detailResult.card);
    const rdescData = parseRdesc(detailResult.rdesc);
    const winner = detailResult.winnat || rdescData.winner || "";
    const winValue = detailResult.win;

    // Determine winner type
    const isHighCard = winValue === "2" || winner.toLowerCase().includes("high");
    const isLowCard = winValue === "1" || winner.toLowerCase().includes("low");

    return {
      roundId: detailResult.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      card: {
        image: cardImage,
        code: detailResult.card,
      },
      resultDetails: {
        winner: winner,
        oddEven: rdescData.oddEven || "",
        color: rdescData.color || "",
        card: rdescData.card || "",
        line: rdescData.line || "",
      },
      isHighCard,
      isLowCard,
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : data.map((res, idx) => ({ win: res, mid: null }));

  return (
    <>
      <div className="result-row lucky7 casino-last-results">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          const winLetter = getWinLetter(win);

          let resultClass = "";
          if (winLetter === "L") resultClass = "result-a";
          else if (winLetter === "H") resultClass = "result-b";
          else if (winLetter === "T") resultClass = "result-c";

          return (
            <div
              key={`${resultItem.mid || index}-${index}`}
              className={`result-circle ${resultClass}`}
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
            >
              {winLetter}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Lucky 7 Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="row bottom-border-casino m-t-10">
            <div className="col-md-12">
              <div className="player-image-container row">
                <div className="text-center player-number col-12">
                  {modalData.card?.image && (
                    <img src={modalData.card.image} alt="Winning Card" onError={(e) => e.target.style.display = 'none'} />
                  )}
                  <div className="col-md-12 text-left">
                    {modalData.resultDetails.winner && (
                      <p className="m-b-0 m-t-20">
                        <b>Winner:</b> {modalData.resultDetails.winner}
                      </p>
                    )}
                    {modalData.resultDetails.oddEven && (
                      <p className="m-b-0">
                        <b>Odd/Even:</b> {modalData.resultDetails.oddEven}
                      </p>
                    )}
                    {modalData.resultDetails.color && (
                      <p className="m-b-0">
                        <b>Color:</b> {modalData.resultDetails.color}
                      </p>
                    )}
                    {modalData.resultDetails.card && (
                      <p className="m-b-0">
                        <b>Card:</b> {modalData.resultDetails.card}
                      </p>
                    )}
                    {modalData.resultDetails.line && (
                      <p className="m-b-0">
                        <b>Line:</b> {modalData.resultDetails.line}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultLucky7;

